// B3.6 / TOUR-4a — session controller: fly between steps, highlight after arrival.

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { useAuth } from "@/contexts/AuthContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import { useActivityDockAssistLevel } from "@/components/activity/useActivityDockAssistLevel";
import { usePrefersReducedMotion } from "@/components/activity/firefly/usePrefersReducedMotion";
import { useFireflyTourTravel } from "@/components/activity/firefly/useFireflyTourTravel";
import { useFireflyTourReturnHome } from "@/components/activity/firefly/useFireflyTourReturnHome";
import { useFireflyTourStepEffect } from "@/components/activity/firefly/useFireflyTourStepEffect";
import {
  createIdleFireflyTourRunnerState,
  reduceFireflyTourRunner,
  resolveTourSessionIntent,
} from "@/lib/firefly/fireflyTourRunner";
import { expandFireflyTourSteps } from "@/lib/firefly/expandFireflyTourSteps";
import { dropLeadingModeSkips } from "@/lib/firefly/fireflyTourStepNavigation";
import { normalizeTourStepCopy } from "@/lib/firefly/normalizeTourStepCopy";
import {
  buildFireflyTourSessionEventDetail,
  emitFireflyTourSessionEvent,
} from "@/lib/firefly/fireflyTourSessionEvent";
import { markFireflyTourCompleted } from "@/lib/firefly/fireflyTourDiscoveryStorage";
import type {
  FireflyTourArtifact,
  FireflyTourRunnerEvent,
  FireflyTourRunnerState,
  FireflyTourTrigger,
} from "@/lib/firefly/tourTypes";
import type { FireflyIntent } from "@/components/activity/firefly/fireflyIntents";

export type FireflyTourDockOverride = {
  active: boolean;
  intent: Extract<FireflyIntent, "teaching" | "discovery" | "guidance">;
};

export type UseFireflyTourSessionOptions = {
  currentMode: PrimaryMode;
  onNavigateToMode: (mode: PrimaryMode) => void;
  onOpenDock?: () => void;
};

export type FireflyTourSessionView = {
  isActive: boolean;
  runner: FireflyTourRunnerState;
  highlightTargetId: string | null;
  highlightReady: boolean;
  isTraveling: boolean;
  travelPosition: { x: number; y: number };
  isReturningHome: boolean;
  travelIntent: Extract<FireflyIntent, "teaching" | "discovery" | "guidance"> | null;
  intent: FireflyTourRunnerState["intent"];
  dockOverride: FireflyTourDockOverride | null;
  stepTitle: string | null;
  stepBody: string | null;
  stepIndex: number;
  stepCount: number;
  canCancel: boolean;
  canAdvance: boolean;
  waitForUserAction: boolean;
  describeOnly: boolean;
  startTour: (artifact: FireflyTourArtifact, trigger: FireflyTourTrigger) => void;
  cancelTour: () => void;
  nextStep: () => void;
};

function runnerReducer(
  state: FireflyTourRunnerState,
  event: FireflyTourRunnerEvent,
): FireflyTourRunnerState {
  return reduceFireflyTourRunner(state, event);
}

export function useFireflyTourSession(
  options: UseFireflyTourSessionOptions,
): FireflyTourSessionView {
  const { currentMode, onNavigateToMode, onOpenDock } = options;
  const auth = useAuth();
  const { anchors } = useAnchorEntitlements();
  const { level: assistLevel } = useActivityDockAssistLevel();
  const reducedMotion = usePrefersReducedMotion();
  const { traveling, position, flyTo, cancelTravel } = useFireflyTourTravel(reducedMotion);
  const [runner, dispatch] = useReducer(runnerReducer, undefined, createIdleFireflyTourRunnerState);
  const [highlightReady, setHighlightReady] = useState(false);
  const timerRef = useRef<number | null>(null);
  const clickCleanupRef = useRef<(() => void) | null>(null);
  const sessionStartedRef = useRef<string | null>(null);
  const lastAnchorCenterRef = useRef<{ x: number; y: number } | null>(null);
  const currentModeRef = useRef(currentMode);
  const authRef = useRef(auth);
  const anchorsRef = useRef(anchors);
  const onNavigateToModeRef = useRef(onNavigateToMode);

  currentModeRef.current = currentMode;
  authRef.current = auth;
  anchorsRef.current = anchors;
  onNavigateToModeRef.current = onNavigateToMode;

  const finalizeTourEnd = useCallback(
    (reason: "cancel" | "complete") => {
      if (reason === "complete" && runner.artifact && runner.trigger && runner.intent) {
        markFireflyTourCompleted(runner.artifact.id);
        emitFireflyTourSessionEvent(
          buildFireflyTourSessionEventDetail({
            tourId: runner.artifact.id,
            phase: "complete",
            trigger: runner.trigger,
            intent: runner.intent,
            mode: currentMode,
            stepIndex: runner.currentIndex,
            stepCount: runner.steps.length,
            assistLevel,
            nowMs: Date.now(),
          }),
        );
      }
      lastAnchorCenterRef.current = null;
      setHighlightReady(false);
      sessionStartedRef.current = null;
      dispatch({ type: reason === "cancel" ? "cancel" : "reset" });
      if (reason === "cancel") {
        dispatch({ type: "reset" });
      }
    },
    [
      assistLevel,
      currentMode,
      runner.artifact,
      runner.currentIndex,
      runner.intent,
      runner.steps.length,
      runner.trigger,
    ],
  );

  const {
    isReturningHome,
    returnTravelIntent,
    beginReturnHome,
    resetReturnHome,
  } = useFireflyTourReturnHome({
    reducedMotion,
    flyTo,
    cancelTravel,
    onFinalize: finalizeTourEnd,
  });

  const tourChromeActive = runner.status === "running" || isReturningHome;

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearClickListener = useCallback(() => {
    clickCleanupRef.current?.();
    clickCleanupRef.current = null;
  }, []);

  const startTour = useCallback(
    (artifact: FireflyTourArtifact, trigger: FireflyTourTrigger) => {
      clearTimer();
      clearClickListener();
      cancelTravel();
      resetReturnHome();
      lastAnchorCenterRef.current = null;
      setHighlightReady(false);
      sessionStartedRef.current = null;
      const expanded: FireflyTourArtifact = {
        ...artifact,
        steps: dropLeadingModeSkips(
          expandFireflyTourSteps(
            artifact.steps,
            currentMode,
            artifact.guideBucketSwitch !== false,
          ),
          currentMode,
        ),
      };
      dispatch({ type: "start", artifact: expanded, trigger });
    },
    [cancelTravel, clearClickListener, clearTimer, currentMode, resetReturnHome],
  );

  const cancelTour = useCallback(() => {
    if (runner.status !== "running" || isReturningHome) return;
    if (runner.artifact && runner.trigger && runner.intent) {
      emitFireflyTourSessionEvent(
        buildFireflyTourSessionEventDetail({
          tourId: runner.artifact.id,
          phase: "abandon",
          trigger: runner.trigger,
          intent: runner.intent,
          mode: currentMode,
          stepIndex: runner.currentIndex,
          stepCount: runner.steps.length,
          assistLevel,
          nowMs: Date.now(),
        }),
      );
    }
    clearTimer();
    clearClickListener();
    setHighlightReady(false);
    void beginReturnHome("cancel", lastAnchorCenterRef.current);
  }, [
    assistLevel,
    beginReturnHome,
    clearClickListener,
    clearTimer,
    currentMode,
    isReturningHome,
    runner.artifact,
    runner.currentIndex,
    runner.intent,
    runner.status,
    runner.steps.length,
    runner.trigger,
  ]);

  const nextStep = useCallback(() => {
    dispatch({ type: "next" });
  }, []);

  useFireflyTourStepEffect({
    currentMode,
    runner,
    dispatch,
    setHighlightReady,
    flyTo,
    cancelTravel,
    clearTimer,
    clearClickListener,
    timerRef,
    clickCleanupRef,
    lastAnchorCenterRef,
    currentModeRef,
    authRef,
    anchorsRef,
    onNavigateToModeRef,
    onOpenDock,
  });

  useEffect(() => {
    if (runner.status !== "running" || !runner.artifact || !runner.trigger) return;
    if (sessionStartedRef.current === runner.artifact.id) return;
    sessionStartedRef.current = runner.artifact.id;
    emitFireflyTourSessionEvent(
      buildFireflyTourSessionEventDetail({
        tourId: runner.artifact.id,
        phase: "start",
        trigger: runner.trigger,
        intent: resolveTourSessionIntent(runner.trigger, runner.artifact),
        mode: currentMode,
        stepIndex: runner.currentIndex,
        stepCount: runner.steps.length,
        assistLevel,
        nowMs: Date.now(),
      }),
    );
  }, [assistLevel, currentMode, runner.artifact, runner.status, runner.steps.length, runner.trigger]);

  useEffect(() => {
    if (runner.status !== "completed" || isReturningHome) return;
    void beginReturnHome("complete", lastAnchorCenterRef.current);
  }, [beginReturnHome, isReturningHome, runner.status]);

  const copy = useMemo(() => {
    if (!runner.currentStep) return { title: null, body: null };
    const normalized = normalizeTourStepCopy(
      runner.currentStep.step.copy,
      (...roles) => auth.hasRole(...roles),
    );
    return { title: normalized.title ?? null, body: normalized.body };
  }, [auth, runner.currentStep]);

  const highlightTargetId =
    runner.status === "running" && runner.currentStep && !isReturningHome
      ? runner.currentStep.step.target.anchorId
      : null;

  const sessionIntent = runner.intent ?? "teaching";
  const activeTravelIntent = returnTravelIntent ?? sessionIntent;
  const dockOverride: FireflyTourDockOverride | null = tourChromeActive
    ? { active: true, intent: activeTravelIntent }
    : null;

  return {
    isActive: tourChromeActive,
    runner,
    highlightTargetId,
    highlightReady: highlightReady && !isReturningHome,
    isTraveling: traveling || isReturningHome,
    travelPosition: position,
    isReturningHome,
    travelIntent: activeTravelIntent,
    intent: runner.intent,
    dockOverride,
    stepTitle: copy.title,
    stepBody: copy.body,
    stepIndex: runner.currentIndex,
    stepCount: runner.steps.length,
    canCancel: runner.canCancel,
    canAdvance: runner.canAdvance,
    waitForUserAction: runner.currentStep?.waitForUserAction ?? false,
    describeOnly: runner.currentStep?.describeOnly ?? false,
    startTour,
    cancelTour,
    nextStep,
  };
}
